import { ClerkProvider } from '@clerk/react'
import { StrictMode } from 'react'
import { createRoot, hydrateRoot } from 'react-dom/client'
import { ErrorBoundary } from 'react-error-boundary'
import { Router } from '#router'
import App from './components/app'
import { PostHogProvider } from './components/posthog-provider'
import { TRPCProvider } from './components/trpc-provider'
import { UserSettingsProvider } from './components/user-settings-context'
import { Sentry } from './lib/sentry-client.js'

const PUBLISHABLE_KEY = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY
const SIGN_IN_URL = '/sign-in'
const SIGN_UP_URL = '/sign-up'
const DEFAULT_FALLBACK_REDIRECT_URL = '/'

if (!PUBLISHABLE_KEY) {
  throw new Error('Missing Clerk Publishable Key')
}

function ErrorFallback() {
  return (
    <div style={{ padding: '2rem', textAlign: 'center' }}>
      <h1>Something went wrong</h1>
      <p>Please refresh the page and try again.</p>
    </div>
  )
}

const container = document.getElementById('root') as HTMLElement
const children = (
  <ErrorBoundary
    FallbackComponent={ErrorFallback}
    onError={(error, info) => {
      Sentry.captureException(error, {
        extra: { componentStack: info.componentStack },
      })
    }}
  >
    <ClerkProvider
      publishableKey={PUBLISHABLE_KEY}
      afterSignOutUrl="/"
      signInUrl={SIGN_IN_URL}
      signUpUrl={SIGN_UP_URL}
      signInFallbackRedirectUrl={DEFAULT_FALLBACK_REDIRECT_URL}
      signUpFallbackRedirectUrl="/problems"
    >
      <PostHogProvider>
        <TRPCProvider>
          <UserSettingsProvider>
            <Router>
              <App />
            </Router>
          </UserSettingsProvider>
        </TRPCProvider>
      </PostHogProvider>
    </ClerkProvider>
  </ErrorBoundary>
)

// Development: Client-side render (no SSR)
// Production: Hydrate server-rendered HTML
if (import.meta.env.DEV) {
  const root = createRoot(container)
  root.render(<StrictMode>{children}</StrictMode>)
} else {
  hydrateRoot(container, children)
}
